import { ActivatedRouteSnapshot } from '@angular/router';
import { Model, ModelUnion } from './base.model';

/**
 * Wrapper interface for generation functions
 * @class interface ModelProductions
 * @attribute make: Function that instantiates a new object from the model
 * @attribute find: Class function find a specific instance of a model
 */
export interface ModelProductions<T extends ModelUnion = ModelUnion> {
    make?: () => T | Promise<T>;
    find: (id: number | string, route?: ActivatedRouteSnapshot) => Promise<T>;
}

/**
 * Factory for dynamically generating or retrieving models
 * @class ModelFactory
 *
 * @param tuples: {
 *          className: string            String of the class name
 *          production: ModelProductions Holds generation functions
 *        }[]
 */
export class ModelFactory {
    /**
     * Map associating the classname strings and their productions
     *
     * @var {Map<string, ModelProductions>}
     */
    private modelMap = new Map<string, ModelProductions>();

    constructor(tuples: { className: string; production: ModelProductions }[]) {
        tuples.map((value) => {
            const { className, production } = value;
            this.modelMap.set(className, production);
        });
    }

    canGenerate(className: string): boolean {
        return this.modelMap.has(className);
    }

    /**
     * Dynamically generate either a fresh instance of an object for a model,
     * or retrieve a specific model via API.
     */
    generate<T extends ModelUnion = Model>(
        className: string,
        id?: number | string,
        route?: ActivatedRouteSnapshot
    ): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            if (!this.modelMap.has(className)) {
                reject();
            }

            const productions = this.modelMap.get(className) as ModelProductions<T>;

            if (id === undefined || id === 'new' || !['string', 'number'].includes(typeof id)) {
                resolve(productions.make());
                return;
            }

            productions
                .find(id, route)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
}
